/**
 * ProShip Direct v2 — the store's public surface.
 *
 * Screens use these hooks and never touch ./store directly, so replacing localStorage with real
 * endpoints later is a change to store.js plus these hooks, not to every page.
 *
 * Nomenclature follows the panel: a courier has a parent courier (Delhivery), a service type
 * (SURFACE, AIR) and a courier code (prozoId). ProShip couriers are the ones we maintain;
 * Direct couriers are contracts the merchant brought themselves.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGetAllCourierPartnersCachedQuery,
  useGetUniqueStoreNamesQuery,
  useGenericPostRequestQuery,
} from '../../../redux/commonRTK';
import { apiConstants, get, getGeneric, postGeneric } from '../../../common/constants';
import { getUserName, getMerchant } from '../../../helpers/ReduxHelpers';
import { useSlice, setSlice, getSlice, resetStore } from './store';
import {
  CREDENTIAL_STATE, SETUP_DIMENSIONS, DEFAULT_LABEL_CONFIG, PRICING_MODELS, findPriorityOption,
  optionForRuleType, priorityOptionByKey,
} from './defaults';
import {
  errText, fetchCatalog, fetchCourierAccount, fetchCourierParents, fetchCourierSchema, fetchMyCouriers, fetchOffers,
  fetchCourierServiceability, fetchServiceTypes, inheritCourierServiceability,
  uploadCourierServiceability, fetchCourierZones, uploadCourierZones, resetCourierZonesToDefault,
  fetchCourierTat, uploadCourierTat, resetCourierTatToDefault,
  fetchSubscription, markOfferViewed, purchasePlan, registerCourier, resolveCourierCp,
  fetchUpgradeQuote, upgradePlanApi, setRenewalPlanApi, clearRenewalPlanApi, renewNowApi,
  respondToOffer, retireCourierApi, setAutoRenewApi, setCourierActive,
  testCourierAccount, testCourierCredentials, checkCourierAccountCode, toPlanCard, updateCourierCredentials,
} from './api';
import { ZONE_ROW_CAP } from './zoneCsv';
import { LANE_ROW_CAP } from './serviceabilityLanes';
import { RATE_ROW_CAP } from './rateCardCsv';
import { findKnob } from './paceKnobs';
import {
  isCoverageFailure, ownCoverage, serviceLineGap, reachGap, opportunityStats,
} from './opportunity';
import { rvpSetup, validateReasons, QC_PERMISSION } from './rvpQc';
import { courierCoverage } from './serviceabilityStats';

/**
 * Fields this module adds to the copied QC model.
 *
 * Kept here rather than in data/rvpQc.js so that file stays a verbatim copy of the admin model.
 * Merged UNDER rvpSetup, so a field the admin model later gains wins over a local stand-in.
 */
const QC_LOCAL_DEFAULTS = { skuExclusions: [] };

export const OWNER = { DIRECT: 'DIRECT', PROSHIP: 'PROSHIP' };

const nextId = (prefix, existing) => `${prefix}-${existing.length + 1}-${Date.now() % 100000}`;

/**
 * How this merchant pays ProShip today — which decides how a Direct plan gets charged.
 *
 * A merchant with a postpaid wallet has a credit line, so activation is a confirmation and the
 * charge lands on their invoice. A prepaid merchant pays from wallet balance now, so activation
 * has to check the balance first and send them to recharge if it is short. Same endpoint the
 * top-bar wallet and the credit page use.
 */
export function useMerchantBilling() {
  const merchant = getMerchant();
  const merchantId = merchant?.id || '';
  const { data, isLoading, isError, refetch } = useGenericPostRequestQuery(
    { type: 'GET', url: apiConstants.GET_ALL_WALLET, data: { merchantId } },
    { skip: !merchantId }
  );

  const wallets = data?.responseObj || [];
  const postpaid = wallets.find((w) => String(w.walletType || '').toLowerCase() === 'postpaid');
  const prepaid = wallets.find((w) => String(w.walletType || '').toLowerCase() !== 'postpaid');

  const creditLimit = Number(postpaid?.creditLimit) || 0;
  const dueAmount = Number(postpaid?.dueAmount) || 0;

  return {
    isPostpaid: Boolean(postpaid),
    walletBalance: Number(prepaid?.walletBalance) || 0,
    creditAvailable: Math.max(creditLimit - dueAmount, 0),
    isLoading,
    isError,
    refetch,
  };
}

/** Blended vs courier-wise pricing. See PRICING_MODELS — this is a merchant-record field. */
export function usePricingModel() {
  const [pricingModel, setPricingModel] = useSlice('pricingModel');
  return {
    pricingModel,
    setPricingModel,
    isBlended: pricingModel === PRICING_MODELS.BLENDED,
  };
}

/**
 * The Direct subscription: which plan, how it was paid for, how much of it is used.
 *
 * Usage is shipments against the plan's allowance, with anything beyond it billed at the plan's
 * over-usage price. Both numbers come from the plan record so the backend can change them
 * without a frontend release.
 */
/**
 * THE merchant's ProShip Direct plan — now served by the backend, not localStorage.
 *
 * The RETURN SHAPE IS UNCHANGED on purpose: `{ plan, usage, activate, deactivate, changePlan,
 * setAutoRenew, resetAll }`, with the same field names the nine consuming screens already
 * render. Swapping the source without touching the screens is the whole point of the hook
 * having been the only store consumer.
 *
 * What changed underneath:
 *   - plan/usage come from GET direct/subscription (which embeds usage), refetched after writes
 *   - activate() is a real purchase: it DEBITS the wallet or the credit line
 *   - setAutoRenew() persists; deactivate() is auto-renew off, so the plan runs out its period
 *   - changePlan() upgrades now (pro-rata, with the email code); setRenewalPlan() picks the plan
 *     for the next period, the only way to a lower one; renewNow() pays a renewal that failed
 *
 * A merchant who is not switched on (MerchantConfig.isProzoDirectEnabled) gets a 400 from every
 * call. That is the master switch answering, not a failure: `plan.active` stays false, the
 * screens render their "not on Direct" state, and `error` carries the sentence if anything wants
 * to show it.
 */
export function usePlan() {
  const [subscription, setSubscription] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, catRes] = await Promise.all([fetchSubscription(), fetchCatalog()]);
      // 200 with a null body is the documented "never bought a plan" answer, not an error.
      setSubscription(subRes?.data || null);
      setCatalog(Array.isArray(catRes?.data) ? catRes.data.map(toPlanCard) : []);
      setError(null);
    } catch (e) {
      setSubscription(null);
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * Buy a plan. Unlike the old local version this can FAIL — an empty wallet, an exhausted
   * credit line, or Direct not being enabled. The error is thrown so the activation modal can
   * show the backend's own sentence ("…short by ₹13,500. Please recharge and try again.")
   * instead of a generic failure.
   */
  const activate = useCallback(async ({ planId, autoRenew = true, proof }) => {
    const card = catalog.find((c) => c.id === planId);
    if (!card) throw new Error('That plan is no longer available. Refresh and try again.');
    try {
      await purchasePlan({ planId: card.dbId, autoRenew, ...proof });
      await load();
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [catalog, load]);

  const setAutoRenew = useCallback(async (value) => {
    try {
      await setAutoRenewApi(!!value);
      await load();
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  /**
   * No merchant-facing cancel endpoint, deliberately: a mid-period cancellation is a refund
   * decision, not a self-service one. Turning auto-renew off is the honest equivalent — the plan
   * runs to the end of the period the merchant already paid for and then stops.
   */
  const deactivate = useCallback(async () => {
    await setAutoRenew(false);
  }, [setAutoRenew]);

  /*
    Changing plan. Up: now, for the pro-rata difference, with the code from the email. Any plan,
    including a lower one: from the next renewal, nothing charged today. Each reloads, so the card
    shows the server's answer rather than an optimistic guess.
  */
  const quoteUpgrade = useCallback(async (planCode) => {
    const card = catalog.find((c) => c.id === planCode);
    if (!card) throw new Error('That plan is no longer available. Refresh and try again.');
    try {
      return (await fetchUpgradeQuote(card.dbId))?.data;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [catalog]);

  const run = useCallback(async (call) => {
    try {
      await call();
      await load();
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  const changePlan = useCallback((planCode, proof) => {
    const card = catalog.find((c) => c.id === planCode);
    return run(() => upgradePlanApi(card?.dbId, proof));
  }, [catalog, run]);

  const setRenewalPlan = useCallback((planCode, keepAccountCodes) => {
    const card = catalog.find((c) => c.id === planCode);
    return run(() => setRenewalPlanApi(card?.dbId, keepAccountCodes || null));
  }, [catalog, run]);

  const clearRenewalPlan = useCallback(() => run(() => clearRenewalPlanApi()), [run]);

  const renewNow = useCallback((proof) => run(() => renewNowApi(proof)), [run]);

  /*
    Map the server's subscription onto the shape the screens already read. The old store kept
    plan.shipmentsUsed; the server is authoritative via usage.used, derived from the allowance
    ledger (an AWB debits one, a cancel-before-pickup credits it back, an RTO debits a second).
  */
  const u = subscription?.usage || {};
  const current = catalog.find((c) => c.id === subscription?.planCode) || null;

  const plan = {
    active: ACTIVE_STATUSES.includes(subscription?.status),
    status: subscription?.status || null,
    planId: subscription?.planCode || null,
    subscriptionNo: subscription?.subscriptionNo || null,
    autoRenew: subscription?.autoRenew ?? false,
    paidFrom: subscription?.paymentMode || null,
    activatedVia: subscription?.activatedVia || null,
    activatedAt: subscription?.activatedAt || null,
    activatedBy: subscription?.activatedBy || null,
    periodStart: u.periodStart || null,
    periodEnd: u.periodEnd || null,
    expiresAt: subscription?.expiresAt || null,
    shipmentsUsed: u.used ?? 0,
    canShip: u.canShip !== false,
    blockedReason: u.blockedReason || null,
    // Renewal: the date, what it will charge, and the plan it moves to when the merchant chose one.
    renewsOn: subscription?.renewsOn || null,
    renewalTotal: subscription?.renewalTotal ?? null,
    nextPlanId: subscription?.nextPlanCode || null,
    nextPlanName: subscription?.nextPlanName || null,
    keepAccountCodes: subscription?.keepAccountCodes || null,
    graceEndsAt: subscription?.graceEndsAt || null,
    // The free trial never renews: the card says when it ends and offers a plan instead.
    trial: Boolean(subscription?.trial),
  };

  // allowance null = unlimited. Keep it null rather than 0 so the card can say the word; the
  // derived numbers fall back to 0/100% only when there IS a finite allowance.
  const allowance = u.allowance ?? null;
  const used = u.used ?? 0;
  const usage = {
    plan: current,
    used,
    allowance,
    unlimited: allowance === null,
    remaining: allowance === null ? null : Math.max(allowance - used, 0),
    overage: allowance === null ? 0 : Math.max(used - allowance, 0),
    percent: allowance ? Math.min(100, Math.round((used / allowance) * 100)) : 0,
    overageCost: Number(u.overageAmount || 0),
    balance: u.balance ?? null,
    daysRemaining: u.daysRemaining ?? null,
  };

  return {
    plan, usage, catalog, loading, error, reload: load,
    activate, deactivate, changePlan, setAutoRenew,
    quoteUpgrade, setRenewalPlan, clearRenewalPlan, renewNow,
    resetAll: resetStore,
  };
}

/**
 * Courier setup, server-driven.
 *
 * DELIBERATELY SEPARATE from useOwnCouriers rather than replacing it. Seven screens consume
 * that hook, and five of them (CourierSetup, Zones, DirectPerformance, SetupPageShell) read
 * fields the backend does not serve yet — per-account config, meta, rate cards, zone maps.
 * Swapping it wholesale would break them to make two screens real. They converge when those
 * endpoints exist.
 *
 * The credential form is NOT hard-coded here. `loadSchema` fetches the field list for the
 * chosen courier and direction, so adding a courier or changing a field is a row in
 * proship_direct_courier_schema, not a release.
 */
export function useDirectCourierSetup() {
  const [parents, setParents] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([fetchCourierParents(), fetchMyCouriers()]);
      setParents(Array.isArray(p?.data) ? p.data : []);
      setAccounts(Array.isArray(a?.data) ? a.data : []);
      setError(null);
    } catch (e) {
      setParents([]); setAccounts([]);
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Step 4. Returns the candidates so the caller can auto-pick a single one or offer a choice. */
  const resolveCp = useCallback(async (courierCode, direction, mode) => {
    try {
      const res = await resolveCourierCp(courierCode, direction, mode);
      return Array.isArray(res?.data) ? res.data : [];
    } catch (e) {
      throw new Error(errText(e));
    }
  }, []);

  /** Step 3b. The service catalogue, narrowed in the form rather than on the server. */
  const loadServiceTypes = useCallback(async (courierCode) => {
    try {
      const res = await fetchServiceTypes(courierCode);
      return Array.isArray(res?.data) ? res.data : [];
    } catch (e) {
      throw new Error(errText(e));
    }
  }, []);

  /** Step 6. The fields to render — never a fixture. */
  const loadSchema = useCallback(async (courierCode, direction) => {
    try {
      const res = await fetchCourierSchema(courierCode, direction);
      return Array.isArray(res?.data) ? res.data : [];
    } catch (e) {
      throw new Error(errText(e));
    }
  }, []);

  /**
   * Steps 5 + 7. Can fail for reasons the merchant must see verbatim: no active plan, the
   * plan's courier limit reached, or PCIS rejecting the credentials.
   */
  const register = useCallback(async (body) => {
    try {
      const res = await registerCourier(body);
      await load();
      return res?.data;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  /**
   * Step 7, and it runs BEFORE step 5 on purpose — see testCourierCredentials.
   *
   * A rejected credential is a RESULT, not an exception: it comes back as { success:false,
   * message } and the screen renders the courier's own words. Only an unreachable server or a
   * refused request throws, and the thrown message is the server's sentence.
   */
  const testCredentials = useCallback(async (body) => {
    try {
      const res = await testCourierCredentials(body);
      return res?.data || { success: false, message: 'The test returned no result.' };
    } catch (e) {
      throw new Error(errText(e));
    }
  }, []);

  /** What the edit drawer opens with: the account's saved limits and masked credentials. */
  const loadAccount = useCallback(async (accountCode) => {
    try {
      const res = await fetchCourierAccount(accountCode);
      return res?.data || null;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, []);

  /** Step 1. { available, message } for the code as typed; throws only when the server is unreachable. */
  const checkAccountCode = useCallback(async (accountCode) => {
    try {
      const res = await checkCourierAccountCode(accountCode);
      return res?.data || null;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, []);

  /**
   * Re-test something already registered, and RECORD the outcome.
   *
   * The server stamps credentialState / lastTestedAt / lastError on the routing document, so the
   * list is reloaded afterwards — otherwise the chip would keep showing the state from before
   * the test the merchant just watched run.
   */
  const testAccount = useCallback(async (accountCode) => {
    try {
      const res = await testCourierAccount(accountCode);
      await load();
      return res?.data || { success: false, message: 'The test returned no result.' };
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  /**
   * Replace the credentials on a registered courier. Tested server-side before it is written.
   *
   * Returns { success, message } — a carrier rejection is a result the drawer renders, and the
   * account keeps its old credentials. Only an unreachable server throws.
   */
  const updateCredentials = useCallback(async (accountCode, body) => {
    try {
      const res = await updateCourierCredentials(accountCode, body);
      await load();
      return res?.data || { success: false, message: 'The update returned no result.' };
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  /**
   * Pause or resume. Also the downgrade mechanism — dropping from five couriers to three
   * switches two off rather than deleting them, so an upgrade later is one call.
   *
   * Re-enabling can be refused when the plan's courier allowance is already used, and the
   * server's sentence is what the caller shows.
   */
  const setActive = useCallback(async (accountCode, value) => {
    try {
      const res = await setCourierActive(accountCode, value);
      await load();
      return res?.data;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  /** Retire for good. Kept on record — shipments already sent on it still resolve to it. */
  const retire = useCallback(async (accountCode) => {
    try {
      const res = await retireCourierApi(accountCode);
      await load();
      return res?.data;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  return {
    parents, accounts, loading, error, reload: load,
    resolveCp, loadSchema, loadServiceTypes, loadAccount, register, testCredentials, checkAccountCode, testAccount,
    updateCredentials,
    setActive, retire,
  };
}

/**
 * The merchant's registered Direct couriers, FROM THE SERVER, in the shape the screens already
 * render.
 *
 * WHY AN ADAPTER AND NOT A REWRITE OF EVERY SCREEN. Home and My Couriers were built against the
 * localStorage fixture, whose account carries `carrierName`, `credentials`, `enabled` and a
 * `config` of four setup dimensions. Three of those four now have a real source; the fourth —
 * which of serviceability, zones, TAT and the rate card the merchant has supplied — has no
 * backend yet, because those uploads are the next piece of work. Mapping here keeps the row
 * components untouched, so when the uploads land only this function changes.
 *
 * `config` IS DELIBERATELY ALL-NULL, not omitted. Null reads as "running on ProShip's default",
 * which is exactly true of a courier registered five minutes ago, and it is what the symbols
 * already render for an unsupplied dimension. The count will say "0 of N settings supplied"
 * until the uploads exist, which is the honest number.
 *
 * CARRIER NAME comes from the parents catalogue when it is loaded, because `courierCode` is
 * XPRESSBEES_REVERSE where a merchant reads "Xpressbees Reverse". Falls back to the code rather
 * than blocking the list on a second request.
 */
/**
 * Does this courier consume one of the plan's slots?
 *
 * ONE DEFINITION, because three screens ask and the server is the fourth. The plan sells how
 * many couriers a merchant can ship on AT ONCE, so a switched-off or retired courier costs
 * nothing — that is what makes the downgrade path work, and what lets a merchant make room
 * without destroying anything. The server counts exactly this set before it allows a
 * registration or a switch back on; a panel that counted differently would disable the Save
 * button on a merchant the server would have accepted, or the reverse.
 *
 * Takes EITHER shape — the raw server account (`isActive`) or the adapted row (`enabled`) — so
 * a screen that already holds one does not have to fetch the other just to count.
 */
export const countsAgainstPlan = (c) =>
  (c.enabled === undefined ? c.isActive : c.enabled) !== false && c.retired !== true;

export function useDirectAccounts() {
  const { parents, accounts, loading, error, reload, testAccount } = useDirectCourierSetup();

  const couriers = useMemo(() => {
    const nameOf = new Map(parents.map((p) => [p.courierCode, p.courierName || p.courierCode]));
    return accounts.map((a) => ({
      id: a.prozoId || a.accountCode,
      /*
        The carrier's name, never displayName: registration stores the account code as the display
        name, so preferring it put "my_urbanebole_test_1" where "UrbaneBolt" belongs — in the
        account picker and in every sentence that says which carrier's coverage is in use.
      */
      carrierName: nameOf.get(a.courierCode) || a.courierCode,
      carrierKey: (a.courierCode || '').toUpperCase(),
      accountCode: a.accountCode,
      serviceType: a.mode,
      direction: a.direction,
      cpId: a.cpId,
      credentials: a.credentialState || CREDENTIAL_STATE.FAILED,
      /*
        Two independent things, and the row needs both: `enabled` is whether the merchant (or a
        plan downgrade) has this courier switched on, `credentials` is whether it can talk to the
        carrier. A courier is shipping only when both hold — which is what the row already says.
      */
      enabled: a.isActive !== false,
      config: { serviceability: null, zones: null, tat: null, rateCard: null },
      meta: {},
      credentialValues: {},
      retired: a.retired === true,
      lastTestedAt: a.lastTestedAt || null,
      lastError: a.lastError || null,
    }));
  }, [parents, accounts]);

  return { couriers, loading, error, reload, testAccount };
}


/**
 * L1 serviceability for ONE Direct courier, from the server.
 *
 * Its own hook rather than part of useDirectCourierSetup: that one loads the parent catalogue and
 * every account on mount, which this screen does not need, and this reloads on a different
 * trigger — after an upload or an inherit, not after a registration.
 *
 * A carrier rejection is not a thing here; the failures are a bad file or a missing default, and
 * both come back as the server's own sentence for the screen to render.
 */
export function useCourierServiceability(accountCode) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!accountCode) { setStatus(null); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetchCourierServiceability(accountCode);
      setStatus(res?.data || null);
      setError(null);
    } catch (e) {
      setStatus(null);
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [accountCode]);

  useEffect(() => { load(); }, [load]);

  const upload = useCallback(async (files) => {
    const res = await uploadCourierServiceability(accountCode, files);
    setStatus(res?.data || null);
    return res?.data;
  }, [accountCode]);

  const inherit = useCallback(async () => {
    const res = await inheritCourierServiceability(accountCode);
    setStatus(res?.data || null);
    return res?.data;
  }, [accountCode]);

  return { status, loading, error, reload: load, upload, inherit };
}

/**
 * Zone mapping for ONE Direct courier, from the server.
 *
 * POLLS WHILE A JOB RUNS. Writing a matrix to S3 is a tracked background job — one file per
 * warehouse for a forward courier, one per customer pincode for a reverse one, so up to ~30,000 —
 * and the screen shows its progress. Polling stops the moment the job leaves IN_PROGRESS, so an
 * idle screen makes no requests.
 */
export function useCourierZones(accountCode) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!accountCode) { setStatus(null); setLoading(false); return; }
    try {
      const res = await fetchCourierZones(accountCode);
      setStatus(res?.data || null);
      setError(null);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [accountCode]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const running = status?.jobProgress === 'IN_PROGRESS';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);

  const upload = useCallback(async (file) => {
    try {
      const res = await uploadCourierZones(accountCode, file);
      setStatus(res?.data || null);
      return res?.data;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [accountCode]);

  /* Not named useDefault: the hooks lint rule treats any use*() call as a hook. */
  const switchToDefault = useCallback(async () => {
    try {
      const res = await resetCourierZonesToDefault(accountCode);
      setStatus(res?.data || null);
      return res?.data;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [accountCode]);

  return { status, loading, error, running, reload: load, upload, switchToDefault };
}

/**
 * TAT for one Direct courier. Not versioned: one per courier, replaced by each upload. Where the
 * merchant has none, or none for a zone, ProShip's TAT for the same courier and service applies, read
 * live; `rows` carries both, each flagged `fromDefault`.
 */
export function useCourierTat(accountCode) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!accountCode) { setStatus(null); setLoading(false); return; }
    try {
      const res = await fetchCourierTat(accountCode);
      setStatus(res?.data || null);
      setError(null);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [accountCode]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const call = useCallback(async (fn) => {
    try {
      const res = await fn();
      setStatus(res?.data || null);
      return res?.data;
    } catch (e) {
      throw new Error(errText(e));
    }
  }, []);

  const upload = useCallback((file) => call(() => uploadCourierTat(accountCode, file)), [accountCode, call]);
  /* Not named useDefault: the hooks lint rule treats any use*() call as a hook. */
  const switchToDefault = useCallback(() => call(() => resetCourierTatToDefault(accountCode)), [accountCode, call]);

  return { status, loading, error, reload: load, upload, switchToDefault };
}

/** Statuses in which the merchant holds a usable plan. GRACE counts — they can still ship. */
/*
  The merchant holds a plan in all three. SUSPENDED (renewal unpaid) is shown on the plan card with
  a way to pay, not as the "buy a plan" page: the slot is still theirs, and a purchase would be
  refused.
*/
const ACTIVE_STATUSES = ['ACTIVE', 'GRACE', 'SUSPENDED'];

/**
 * Offers published to this merchant — the inbox for a negotiated price.
 *
 * Two kinds arrive on the same endpoint and the UI does not need to care which: a PUBLIC
 * promotion every merchant can take, and a TARGETED deal a KAM built for this merchant alone.
 * `myStatus` is present only on the targeted ones (PENDING / VIEWED), because a public offer has
 * no per-merchant row until someone accepts it.
 *
 * ACCEPTING BUYS. respond({accept:true}) goes through the same purchase path as the catalog —
 * wallet debit and all — so it throws the server's own sentence on a decline, and the caller
 * must await it.
 */
export function useDirectOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchOffers();
      setOffers(Array.isArray(res?.data) ? res.data : []);
      setError(null);
    } catch (e) {
      // A merchant not on Direct gets a 400 here; that is the master switch, not a fault.
      setOffers([]);
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /*
    Stamp first view. Fire-and-forget on purpose: sales wants to know an offer was seen, but a
    failed stamp must never stop the merchant reading it.
  */
  const markViewed = useCallback((offerId) => {
    markOfferViewed(offerId).catch(() => {});
  }, []);

  const respond = useCallback(async (offerId, accept, declinedReason, proof) => {
    try {
      await respondToOffer(offerId, { accept, declinedReason: declinedReason || null, ...(proof || {}) });
      await load();
    } catch (e) {
      throw new Error(errText(e));
    }
  }, [load]);

  return { offers, loading, error, reload: load, markViewed, respond };
}

/** The merchant's own (Direct) courier accounts. */
/**
 * The merchant's own courier accounts.
 *
 * `couriers` deliberately excludes retired accounts. An account cannot be deleted — shipments,
 * COD remittances and invoices reference it, and a rule may name it — so retiring sets a flag and
 * locks it off for good. Every consumer of this hook (allocation pools, setup pages, the rules
 * courier list, the home page) wants the accounts that can still ship, so they get exactly those
 * without each having to remember to filter. `retired` is there for the one screen that shows
 * history, and `allCouriers` for anything that must resolve an account code from a past shipment.
 */
export function useOwnCouriers() {
  const [allCouriers, setCouriers] = useSlice('ownCouriers');
  const couriers = allCouriers.filter((c) => !c.retired);
  const retired = allCouriers.filter((c) => c.retired);

  const addCourier = useCallback(
    (account) =>
      setCouriers((list) => [
        ...list,
        {
          id: nextId('own', list),
          enabled: account.credentials === CREDENTIAL_STATE.TESTED,
          config: { serviceability: null, zones: null, tat: null, rateCard: null },
          ...account,
        },
      ]),
    [setCouriers]
  );

  const updateCourier = useCallback(
    (id, patch) =>
      setCouriers((list) =>
        list.map((c) => {
          if (c.id !== id) return c;
          // Credentials and contract limits decide what allocation may send here, so a change is
          // audited the same way switching the account off is.
          logActivity({
            entity: ACTIVITY_ENTITY.COURIER,
            name: `${c.carrierName} · ${c.accountCode}`,
            action: 'UPDATED',
            detail: [
              patch.credentialValues ? 'credentials' : null,
              patch.meta ? 'contract limits' : null,
              patch.serviceType && patch.serviceType !== c.serviceType ? `service → ${patch.serviceType}` : null,
            ]
              .filter(Boolean)
              .join(', ') || 'account details',
          });
          return { ...c, ...patch };
        })
      ),
    [setCouriers]
  );

  /**
   * Retires an account: off for allocation, permanently, and never deleted.
   *
   * Deleting the row would orphan every shipment that carries this account code — tracking,
   * remittance and weight disputes all resolve back through it — so the record stays and the
   * account stops being offered. There is no un-retire: a merchant who wants the contract back
   * connects it again, which gets fresh credentials and a fresh test rather than reviving a row
   * whose secrets may be years stale.
   */
  const retireCourier = useCallback(
    (id) =>
      setCouriers((list) =>
        list.map((c) => {
          if (c.id !== id) return c;
          logActivity({
            entity: ACTIVITY_ENTITY.COURIER,
            name: `${c.carrierName} · ${c.accountCode}`,
            action: 'RETIRED',
            detail: 'Permanently disabled for allocation. Shipments already sent on it are unaffected.',
          });
          return { ...c, retired: true, retiredAt: new Date().toISOString(), retiredBy: getUserName() || '—', enabled: false };
        })
      ),
    [setCouriers]
  );

  const setConfig = useCallback(
    (id, dimension, provided) =>
      setCouriers((list) =>
        list.map((c) =>
          c.id === id ? { ...c, config: { ...c.config, [dimension]: provided ? 'OWN' : null } } : c
        )
      ),
    [setCouriers]
  );

  const toggleEnabled = useCallback(
    (id) => setCouriers((list) => list.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c))),
    [setCouriers]
  );

  return {
    couriers,
    retired,
    allCouriers,
    addCourier,
    updateCourier,
    retireCourier,
    setConfig,
    toggleEnabled,
  };
}

/**
 * ProShip's courier catalogue — parent courier, service type and courier code, live from dev.
 * Same endpoint and field set the panel's own useCourierServiceType uses.
 */
export function useCourierCatalogue() {
  const { data, isLoading, isError, refetch } = useGetAllCourierPartnersCachedQuery({
    andfilter: [
      { field: 'is_deleted', operator: 'eq', value: '0' },
      { field: 'isActive', operator: 'eq', value: true },
      { field: 'isExternal', operator: 'eq', value: false },
    ],
    offset: 0,
    limit: 500,
    skipCount: true,
    /*
      logoUrl is one more field on the same courier document — it is already in the field
      list of the panel's persisted courier query and has simply never been rendered. Asking
      for it here costs no extra request, and if the backend has nothing to give the key is
      absent and CourierLogo falls back to a lettered tile.
    */
    includes: 'id,name,parent,prozoId,supportedShipmentType,serviceTypePrimary,logoUrl,minChargeableWeight',
  });

  const couriers = useMemo(
    () =>
      (Array.isArray(data) ? data : [])
        .filter((c) => c.prozoId)
        .map((c) => ({
          code: c.prozoId,
          name: c.name,
          parent: c.parent || c.name,
          serviceType: c.serviceTypePrimary || '',
          shipmentType: c.supportedShipmentType || '',
          logo: c.logoUrl || '',
          /*
            The courier's own minimum chargeable weight, in grams — courier meta, not a rate
            card. A rate card is what the merchant pays; this is the slab the carrier bills
            from, and it belongs to the courier whether or not a card has been uploaded.

            NAMED FOR THE FIELD THIS MODULE'S OWN COURIER FORM USES (COURIER_META_GROUPS in
            defaults.js), so Direct and ProShip rows mean the same thing by it. If the courier
            document spells it differently the key is simply absent and the column reads "—";
            it cannot break the row.
          */
          minChargeableWeight: c.minChargeableWeight ?? null,
        })),
    [data]
  );

  return { couriers, isLoading, isError, refetch };
}

/**
 * Who switched what, and when.
 *
 * Rules and couriers are both things a merchant turns off to change where orders go, and both
 * are worth an audit for the same reason: when allocation changes on a Tuesday, somebody needs to
 * be able to see that a courier was disabled on Monday and by whom. `createdBy` on a rule only
 * covers who wrote it, not who later switched it off.
 *
 * Written by the hooks below rather than by the screens, so nothing can change state without
 * being recorded.
 */
export const ACTIVITY_ENTITY = { RULE: 'RULE', COURIER: 'COURIER', PLAN: 'PLAN' };

export function useActivityLog(entity) {
  const [activity] = useSlice('activity');

  const entries = useMemo(
    () => (entity ? activity.filter((a) => a.entity === entity) : activity),
    [activity, entity]
  );

  return { entries };
}

/** Append one entry. Not a hook — called from inside the state setters below. */
function logActivity({ entity, name, action, detail }) {
  setSlice('activity', (list) =>
    [
      {
        id: `act-${list.length + 1}-${Date.now() % 100000}`,
        at: new Date().toISOString(),
        user: getUserName() || '—',
        entity,
        name,
        action,
        detail: detail || '',
      },
      ...list,
    ].slice(0, 200)
  );
}

/** Pincodes the merchant has blocked, per courier, per category. */
/*
 * useTrackingPage removed with the screen it served — the tracking page is now
 * src/app/components/tracking/TrackingConfig.js, which reads and writes the merchant record
 * through merchantDesc/config/tracking rather than this module's localStorage store.
 */

/**
 * The rate card for one account.
 *
 * Same shape as useServiceabilityLanes and useZoneMap — three screens doing the same job should
 * not have three different APIs.
 */
export function useRateCard(accountId) {
  const [cards, setCards] = useSlice('rateCards');

  const save = useCallback(
    (slabs, summary, fileName) =>
      setCards((all) => ({
        ...all,
        [accountId]: {
          slabs: slabs.slice(0, RATE_ROW_CAP),
          summary,
          fileName,
          uploadedAt: new Date().toISOString(),
          uploadedBy: getUserName() || '—',
        },
      })),
    [accountId, setCards]
  );

  const clear = useCallback(
    () =>
      setCards((all) => {
        const next = { ...all };
        delete next[accountId];
        return next;
      }),
    [accountId, setCards]
  );

  return { card: cards?.[accountId] || null, save, clear };
}

/**
 * The coverage file for one account.
 *
 * Deliberately the same shape as useZoneMap: two screens that do the same job — validate a CSV,
 * store it against one account, hand it back for download — should not have two different APIs.
 */
export function useServiceabilityLanes(accountId) {
  const [files, setFiles] = useSlice('serviceabilityLanes');

  const save = useCallback(
    (rows, summary) =>
      setFiles((all) => ({
        ...all,
        [accountId]: {
          rows: rows.slice(0, LANE_ROW_CAP),
          summary,
          uploadedAt: new Date().toISOString(),
          uploadedBy: getUserName() || '—',
        },
      })),
    [accountId, setFiles]
  );

  const clear = useCallback(
    () =>
      setFiles((all) => {
        const next = { ...all };
        delete next[accountId];
        return next;
      }),
    [accountId, setFiles]
  );

  return { file: files?.[accountId] || null, save, clear };
}

/**
 * The pincode-level zone map for one account.
 *
 * Saving also flips config.zones to OWN, because a lane list IS a zone map — leaving the account
 * showing "ProShip default" after an upload would be the screen contradicting itself.
 */
export function useZoneMap(accountId) {
  const [maps, setMaps] = useSlice('zoneMaps');

  const save = useCallback(
    (rows, summary) =>
      setMaps((all) => ({
        ...all,
        [accountId]: {
          rows: rows.slice(0, ZONE_ROW_CAP),
          summary,
          uploadedAt: new Date().toISOString(),
          uploadedBy: getUserName() || '—',
        },
      })),
    [accountId, setMaps]
  );

  const clear = useCallback(
    () =>
      setMaps((all) => {
        const next = { ...all };
        delete next[accountId];
        return next;
      }),
    [accountId, setMaps]
  );

  return { map: maps?.[accountId] || null, save, clear };
}

export function useBlockedPincodes() {
  const [blocked, setBlocked] = useSlice('blocked');
  const [history, setHistory] = useSlice('uploadHistory');

  const forCourier = useCallback((code) => blocked[code] || {}, [blocked]);

  /** Block or unblock a list of pincodes and record it in the audit. */
  const apply = useCallback(
    ({ code, courierName, category, action, pincodes, fileName, user }) => {
      setBlocked((all) => {
        const current = all[code] || {};
        const existing = current[category] || [];
        const next =
          action === 'BLOCK'
            ? [...new Set([...existing, ...pincodes])]
            : existing.filter((p) => !pincodes.includes(p));
        return { ...all, [code]: { ...current, [category]: next } };
      });

      setHistory((list) =>
        [
          {
            id: nextId('upload', list),
            at: new Date().toISOString(),
            fileName: fileName || 'Entered manually',
            count: pincodes.length,
            courierName,
            code,
            action,
            category,
            user: user || '—',
            status: 'COMPLETED',
          },
          ...list,
        ].slice(0, 50)
      );
    },
    [setBlocked, setHistory]
  );

  return { blocked, forCourier, apply, history };
}

/**
 * Which couriers allocation may use, in one list — ProShip's catalogue plus the merchant's
 * Direct accounts, each with its coverage after uploads and blocks.
 */
export function useCourierAvailability() {
  const { couriers: catalogue, isLoading, isError, refetch } = useCourierCatalogue();
  const { couriers: direct, toggleEnabled } = useOwnCouriers();
  const { plan } = usePlan();
  const [disabledProzo, setDisabled] = useSlice('disabledProzo');
  const { blocked } = useBlockedPincodes();

  const toggleProzo = useCallback(
    (code) =>
      setDisabled((list) => (list.includes(code) ? list.filter((c) => c !== code) : [...list, code])),
    [setDisabled]
  );

  /**
   * Enable or disable a specific set of rows — whatever the screen currently has filtered and
   * tabbed, both pools together. One control, so a merchant who has filtered to Air couriers
   * switches exactly those and nothing else.
   *
   * Direct accounts whose credentials have not passed are skipped: they cannot ship, and
   * flipping their flag would show them as enabled while allocation ignores them.
   */
  const setRowsEnabled = useCallback(
    (rows, enabled) => {
      // One entry for the batch, naming how many — 114 separate lines would bury the log.
      if (rows.length) {
        logActivity({
          entity: ACTIVITY_ENTITY.COURIER,
          name: `${rows.length} courier${rows.length === 1 ? '' : 's'}`,
          action: enabled ? 'ENABLED' : 'DISABLED',
          detail: 'Bulk change from the courier list',
        });
      }

      const proshipCodes = rows.filter((r) => r.owner === OWNER.PROSHIP).map((r) => r.code);
      if (proshipCodes.length) {
        setDisabled((list) =>
          enabled
            ? list.filter((code) => !proshipCodes.includes(code))
            : [...new Set([...list, ...proshipCodes])]
        );
      }

      const directIds = rows
        .filter((r) => r.owner === OWNER.DIRECT && !r.blockedFor)
        .map((r) => r.accountId);
      if (directIds.length) {
        setSlice('ownCouriers', (list) =>
          list.map((c) => (directIds.includes(c.id) ? { ...c, enabled } : c))
        );
      }
    },
    [setDisabled]
  );

  /** One row shape for both pools, so the table and the cards read the same thing. */
  const rows = useMemo(() => {
    /*
      A DIRECT ACCOUNT IS STILL A CARRIER'S PARCEL.

      Own accounts are rows the merchant created, so they carry no logoUrl of their own — but
      a Direct Delhivery account is Delhivery, and showing it as a blank letter beside the
      ProShip Delhivery row would read as two different carriers. Matching on the carrier name
      lends it the parent's mark. Lower-cased because a merchant types the name by hand.
    */
    const logoByParent = new Map(
      catalogue
        .filter((c) => c.logo)
        .map((c) => [String(c.parent || '').toLowerCase(), c.logo])
    );
    const proship = catalogue.map((c) => ({
      id: `proship:${c.code}`,
      owner: OWNER.PROSHIP,
      code: c.code,
      parent: c.parent,
      name: c.name,
      serviceType: c.serviceType,
      shipmentType: c.shipmentType,
      logo: c.logo,
      minChargeableWeight: c.minChargeableWeight,
      enabled: !disabledProzo.includes(c.code),
      blockedFor: false,
      coverage: courierCoverage(c.code, blocked[c.code]),
    }));

    const own = direct.map((c) => ({
      id: `direct:${c.id}`,
      owner: OWNER.DIRECT,
      accountId: c.id,
      code: c.accountCode,
      parent: c.carrierName,
      name: `${c.carrierName} ${c.serviceType}`,
      serviceType: (c.serviceType || '').toUpperCase(),
      shipmentType: 'B2C',
      logo: logoByParent.get(String(c.carrierName || '').toLowerCase()) || '',
      /* Off the account's own meta — what the merchant entered on the courier form. */
      minChargeableWeight: c.meta?.minChargeableWeight ?? null,
      enabled: c.enabled && c.credentials === CREDENTIAL_STATE.TESTED,
      blockedFor: c.credentials !== CREDENTIAL_STATE.TESTED,
      credentials: c.credentials,
      // Uploaded coverage replaces ProShip's; otherwise the parent courier's is inherited.
      coverage: courierCoverage(
        c.config?.serviceability === 'OWN' ? c.accountCode : c.carrierName,
        blocked[c.accountCode],
        c.config?.serviceability === 'OWN' ? 9000 + (c.accountCode.length * 311) % 6000 : null
      ),
    }));

    /*
      NO DIRECT COURIERS WITHOUT THE PLAN. Everything downstream of this list — the courier
      settings page, the rule builder's courier pool, the priority picker, the simulator — reads
      these rows, so gating here gates all of them at once. Filtering at each screen would leave
      the next one someone writes exposed by default, which is the wrong way round.

      They are hidden, not deleted: the accounts stay in the store, so reactivating the plan
      brings back a merchant's couriers and their uploaded files exactly as they were.
    */
    return plan.active ? [...own, ...proship] : proship;
  }, [catalogue, direct, disabledProzo, blocked, plan.active]);

  const toggleRow = useCallback(
    (row) => {
      logActivity({
        entity: ACTIVITY_ENTITY.COURIER,
        name: `${row.parent} ${row.serviceType || ''}`.trim(),
        action: row.enabled ? 'DISABLED' : 'ENABLED',
        detail: row.code,
      });
      return row.owner === OWNER.DIRECT ? toggleEnabled(row.accountId) : toggleProzo(row.code);
    },
    [toggleEnabled, toggleProzo]
  );

  return {
    rows,
    toggleRow,
    setRowsEnabled,
    disabledProzo,
    isLoading,
    isError,
    refetch,
  };
}

/**
 * Allocation rules — append-only.
 *
 * A rule can be created, cloned, reordered and switched off. It can never be edited or deleted,
 * because a rule that has allocated orders is a record of why those orders went where they did:
 * rewriting it would silently rewrite that history, and deleting it would erase it. Changing your
 * mind means switching one off and creating its replacement, which leaves the audit intact.
 */
export function useShippingRules() {
  const [rules, setRules] = useSlice('rules');

  const createRule = useCallback(
    (rule) =>
      setRules((list) => {
        const now = new Date().toISOString();
        logActivity({ entity: ACTIVITY_ENTITY.RULE, name: rule.name, action: 'CREATED', detail: 'Rule' });
        return [
          ...list,
          {
            order: [],
            enabled: true,
            fallback: true,
            ...rule,
            id: nextId('rule', list),
            createdBy: getUserName() || '—',
            createdOn: now,
            lastModified: now,
          },
        ];
      }),
    [setRules]
  );

  /** Clone gives back a draft — same conditions and strategy, nothing persisted until saved. */
  const draftFromRule = useCallback(
    (id) => {
      const source = rules.find((r) => r.id === id);
      if (!source) return null;
      return {
        name: `${source.name} (copy)`,
        conditions: source.conditions.map((c) => ({ ...c })),
        strategy: source.strategy,
        order: [...(source.order || [])],
      };
    },
    [rules]
  );

  const setStatus = useCallback(
    (id, field, value) =>
      setRules((list) =>
        list.map((r) => {
          if (r.id !== id) return r;
          logActivity({
            entity: ACTIVITY_ENTITY.RULE,
            name: r.name,
            action: value ? 'ENABLED' : 'DISABLED',
            detail: field === 'fallback' ? 'Fallback' : 'Rule',
          });
          return { ...r, [field]: value, lastModified: new Date().toISOString() };
        })
      ),
    [setRules]
  );

  const moveRule = useCallback(
    (id, direction) =>
      setRules((list) => {
        const from = list.findIndex((r) => r.id === id);
        const to = from + direction;
        if (from < 0 || to < 0 || to >= list.length) return list;
        const next = [...list];
        [next[from], next[to]] = [next[to], next[from]];
        return next;
      }),
    [setRules]
  );

  const reorderRules = useCallback(
    (orderedIds) =>
      setRules((list) => orderedIds.map((id) => list.find((r) => r.id === id)).filter(Boolean)),
    [setRules]
  );

  return { rules, createRule, draftFromRule, setStatus, moveRule, reorderRules };
}

/** PACE weighting — one set, no longer per mode. */
/**
 * PACE knobs for this merchant.
 *
 * Mirrors the real scope model: `overrides` is sparse, and anything absent inherits ProShip's
 * global default from the catalogue. `valueOf` is therefore the only correct way to read a knob —
 * reading overrides directly would show a blank where a working default is in force.
 *
 * Every commit records a reason and appends to history, because the real
 * PUT /api/allocation/config/knobs refuses a change without one and the store it writes to is
 * append-only. A screen that let a merchant save silently would not survive that endpoint.
 *
 * TODO(ravi, 2026-09): there is no merchant-facing endpoint for these. The admin panel calls
 * GET/PUT allocation/config/knobs with scope=merchant#<id> behind a ProshipAdmin gate, so wiring
 * this up is (a) opening that scope to the merchant's own id and (b) swapping this hook's body for
 * the three calls in proship-backend/.../decisionConfigService.js. The catalogue itself should come
 * from PACE's GET /pace/v1/admin/knob-catalog rather than the mirror in data/paceKnobs.js.
 */
export function usePaceKnobs() {
  const [pace, setPace] = useSlice('pace');

  // Memoised because the `|| {}` fallback would otherwise hand valueOf and isOverridden a fresh
  // object on every render, defeating their useCallback entirely.
  const overrides = useMemo(() => pace.overrides || {}, [pace.overrides]);

  /** Effective value: the merchant's override if set, otherwise ProShip's global default. */
  const valueOf = useCallback(
    (key) => (overrides[key] !== undefined ? overrides[key] : findKnob(key)?.default),
    [overrides]
  );

  const isOverridden = useCallback((key) => overrides[key] !== undefined, [overrides]);

  /** Applies a batch of edits with one audited reason, and records what changed. */
  const commit = useCallback(
    (edits, reason) =>
      setPace((prev) => {
        const before = prev.overrides || {};
        const entries = Object.entries(edits);
        if (!entries.length) return prev;

        const nextOverrides = { ...before };
        const audit = [];

        entries.forEach(([key, value]) => {
          const knob = findKnob(key);
          const from = before[key] !== undefined ? before[key] : knob?.default;
          if (value === null) {
            delete nextOverrides[key];
          } else {
            nextOverrides[key] = Number(value);
          }
          audit.push({
            key,
            label: knob?.label || key,
            from,
            to: value === null ? knob?.default : Number(value),
            reset: value === null,
            reason,
            by: getUserName() || '—',
            at: new Date().toISOString(),
          });
        });

        return {
          ...prev,
          overrides: nextOverrides,
          history: [...audit.reverse(), ...(prev.history || [])].slice(0, 200),
        };
      }),
    [setPace]
  );

  const setProfile = useCallback(
    (profile) =>
      setPace((prev) => ({
        ...prev,
        profile,
        history: [
          {
            key: 'profile',
            label: 'Allocation profile',
            from: prev.profile,
            to: profile,
            reason: 'Profile changed',
            by: getUserName() || '—',
            at: new Date().toISOString(),
          },
          ...(prev.history || []),
        ].slice(0, 200),
      })),
    [setPace]
  );

  const resetAllKnobs = useCallback(
    (reason) =>
      setPace((prev) => ({
        ...prev,
        overrides: {},
        history: [
          {
            key: 'all',
            label: 'All knobs',
            from: 'your values',
            to: 'ProShip defaults',
            reason,
            by: getUserName() || '—',
            at: new Date().toISOString(),
          },
          ...(prev.history || []),
        ].slice(0, 200),
      })),
    [setPace]
  );

  return {
    profile: pace.profile || 'weighted',
    overrides,
    history: pace.history || [],
    valueOf,
    isOverridden,
    commit,
    setProfile,
    resetAllKnobs,
  };
}

/**
 * Label configuration for one scope — DEFAULT, or a channel that may override it.
 *
 * Inheritance is real, not a copy: a channel with no override reads the default live, so editing
 * the default updates every channel that has not been customised. `isInherited` tells the screen
 * which of the two it is looking at, because "same as default" and "overridden to the same
 * values" behave differently the next time the default changes.
 */
export const LABEL_DEFAULT_SCOPE = 'default';

/**
 * The merchant's connected sales channels — the stores their orders actually arrive from.
 *
 * Offering label overrides for every channel ProShip integrates with would be a list of screens
 * a merchant will never use, and it invites them to configure a channel they are not selling on.
 * `channel/order/unique-store-names` is the panel's own answer to "which stores are you
 * connected to", already used by COD On Hold.
 *
 * The fallback list is for a merchant with no channel orders yet (a dev account, or an
 * API-only seller) — it keeps the screen demonstrable rather than empty.
 */
const FALLBACK_CHANNELS = ['Shopify', 'Unicommerce', 'Vinculum', 'Magento'];

export function useConnectedChannels() {
  const merchant = getMerchant();
  const merchantOid = merchant?.id || '';
  const { data, isLoading, isError } = useGetUniqueStoreNamesQuery(merchantOid, { skip: !merchantOid });

  const channels = useMemo(() => {
    const live = (Array.isArray(data) ? data : []).filter(Boolean);
    return live.length ? live : FALLBACK_CHANNELS;
  }, [data]);

  const isFallback = !(Array.isArray(data) && data.filter(Boolean).length > 0);

  return { channels, isFallback, isLoading, isError };
}

export function useLabelConfig(scope = LABEL_DEFAULT_SCOPE) {
  const [store, setStore] = useSlice('labelConfig');

  const isDefaultScope = scope === LABEL_DEFAULT_SCOPE;
  const override = isDefaultScope ? null : store.channels?.[scope];
  const isInherited = !isDefaultScope && !override;
  const config = isDefaultScope ? store.default : override || store.default;

  /** Writing to an inheriting channel forks it from the default at that moment. */
  const write = useCallback(
    (updater) =>
      setStore((s) => {
        if (isDefaultScope) return { ...s, default: updater(s.default) };
        const base = s.channels?.[scope] || s.default;
        return { ...s, channels: { ...s.channels, [scope]: updater(base) } };
      }),
    [setStore, scope, isDefaultScope]
  );

  const set = useCallback((patch) => write((c) => ({ ...c, ...patch })), [write]);

  const toggleField = useCallback(
    (key, exclusions = []) =>
      write((c) => {
        const next = { ...c.fields, [key]: !c.fields[key] };
        // Turning one side of an exclusive pair on turns the other off, rather than refusing the
        // click and leaving the merchant to work out why.
        if (next[key]) {
          exclusions.forEach((pair) => {
            if (pair.includes(key)) pair.filter((k) => k !== key).forEach((other) => { next[other] = false; });
          });
        }
        return { ...c, fields: next };
      }),
    [write]
  );

  const setAllInGroup = useCallback(
    (keys, value) =>
      write((c) => ({
        ...c,
        fields: keys.reduce((acc, k) => ({ ...acc, [k]: value }), { ...c.fields }),
      })),
    [write]
  );

  /** Drop a channel's override so it follows the default again. */
  const resetToDefault = useCallback(
    () =>
      setStore((s) => {
        if (isDefaultScope) return { ...s, default: DEFAULT_LABEL_CONFIG };
        const channels = { ...s.channels };
        delete channels[scope];
        return { ...s, channels };
      }),
    [setStore, scope, isDefaultScope]
  );

  const overriddenChannels = Object.keys(store.channels || {});

  return { config, isInherited, isDefaultScope, set, toggleField, setAllInGroup, resetToDefault, overriddenChannels };
}

/** Returns and QC configuration. */
/**
 * The default allocation preference — a real merchant setting, not local state.
 *
 * Read comes off the merchant record as `mappedOrderRule.type`, which is where the panel already
 * reads it (see components/Merchant Rule/Rules.js). Write goes to ruleengine/setMerchantRule, the
 * same endpoint the page this replaces used.
 *
 * Deliberately does NOT touch the PACE profile. Picking PACE here says "let the engine decide";
 * what the engine then weighs is a business knob and belongs in one place, PACE Engine.
 */
export function useCourierPriority() {
  const merchant = getMerchant();
  const merchantId = merchant?.id || '';

  const [local, setLocal] = useSlice('courierPriority');
  const [ruleType, setRuleType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!merchantId) return;
    setLoading(true);
    setError(null);
    get(`${apiConstants.BASE_LOCAL}${apiConstants.GET_MERCHANT}/${merchantId}`)
      .then((res) => setRuleType(res?.data?.mappedOrderRule?.type || null))
      .catch(() => setError('Could not read your current preference.'))
      .finally(() => setLoading(false));
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Resolves once the engine has accepted the change, so the caller can confirm honestly. */
  const choose = useCallback(
    async (key) => {
      const option = findPriorityOption(key);
      if (!merchantId) return false;

      // An option with no ruleType is one the engine has no value for (Fastest, Personalised), so
      // it is held locally instead of posted. Guarded here rather than at the call site so that no
      // future caller can send an enum the engine rejects — which is how this screen broke once.
      if (!option.ruleType) {
        setLocal({ ...local, chosen: option.key });
        logActivity({
          entity: ACTIVITY_ENTITY.RULE,
          name: 'Courier priority',
          action: 'UPDATED',
          detail: `Default allocation set to ${option.label} (held locally — not yet supported by the rule engine)`,
        });
        return true;
      }

      setSaving(true);
      setError(null);
      try {
        const res = await postGeneric(apiConstants.SET_MERCHANT_RULE, {
          merchantOid: merchantId,
          ruleType: option.ruleType,
        });
        if (res.status !== 200) throw new Error(res?.data?.message || 'rejected');
        setRuleType(option.ruleType);
        if (local?.chosen) setLocal({ ...local, chosen: null });
        logActivity({
          entity: ACTIVITY_ENTITY.RULE,
          name: 'Courier priority',
          action: 'UPDATED',
          detail: `Default allocation set to ${option.label}`,
        });
        return true;
      } catch (err) {
        setError(err?.response?.data?.message || 'The rule engine did not accept that change.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [merchantId, local, setLocal]
  );

  /** Replaces the whole order. Local only — see the note on COURIER_PRIORITY_OPTIONS. */
  const setOrder = useCallback(
    (order) => setLocal({ ...local, order, savedAt: new Date().toISOString() }),
    [local, setLocal]
  );

  /*
   * Which card is lit, in precedence order:
   *   1. a locally chosen option, because picking Fastest or Personalised cannot clear a ruleType
   *      the engine still holds — without this the merchant's click would appear not to register
   *   2. whatever the merchant record says, including CUSTOM, which lights Personalised
   * A stored key that no longer matches a card resolves to null rather than to the first one.
   */
  const option =
    (local?.chosen && priorityOptionByKey(local.chosen)) || optionForRuleType(ruleType) || null;

  return {
    ruleType,
    option,
    order: local?.order || [],
    setOrder,
    savedAt: local?.savedAt || null,
    loading,
    saving,
    error,
    choose,
    reload: load,
  };
}
/**
 * Returns and doorstep QC.
 *
 * One policy per merchant, applied to every reverse pickup on every courier — so unlike coverage
 * or rates there is no account to scope it to. `setup` reads through to the grounded seed until
 * the merchant changes something, which keeps the store sparse and means a new field added to the
 * model appears with its real default rather than as undefined.
 *
 * TODO(ravi, 2026-09): replaced by /api/qc/*. That contract is not frozen — the admin module runs
 * on mocks behind its own USE_MOCK flag — so this screen is at the same maturity as ops, not behind
 * it. Editability (VIEW_ONLY / VIEW_EDIT / HIDDEN) is granted by ops and already exists admin-side;
 * QC_PERMISSION stands in for that read.
 */
export function useReturnsQc() {
  const [stored, setStored] = useSlice('returnsQc');
  /**
   * Merged over the seed, not taken wholesale.
   *
   * A stored object missing `selected` or `reasons` — which is what a slice written under an older
   * schema looks like — would otherwise reach the screen and throw on first access. Filling from
   * the seed means a partial slice degrades to defaults instead of a blank page.
   */
  const setup = { ...QC_LOCAL_DEFAULTS, ...rvpSetup, ...(stored || {}) };

  /** Structural edits are easier to express as a mutation than as a spread three levels deep. */
  const patch = useCallback(
    (mutate) =>
      setStored((prev) => {
        const next = JSON.parse(JSON.stringify(prev || rvpSetup));
        mutate(next);
        return next;
      }),
    [setStored]
  );

  const errors = setup.type === 'reason' ? validateReasons(setup.reasons) : [];

  return { setup, patch, errors, editable: QC_PERMISSION === 'VIEW_EDIT' };
}

/** The pool a rule can order: every enabled courier, Direct first. */
export function useCourierPool() {
  const { rows, isLoading, isError } = useCourierAvailability();

  const pool = useMemo(
    () =>
      rows
        .filter((r) => r.enabled)
        .map((r) => ({
          key: `${r.owner.toLowerCase()}:${r.code}`,
          name: r.parent,
          detail: `${r.serviceType || '—'} · ${r.code}`,
          owner: r.owner,
        })),
    [rows]
  );

  /*
    WHAT THE POOL IS LEAVING OUT, and why.

    A rule or a priority list can only name a courier that is switched on, so the pool filters to
    enabled — which is right, and silently confusing. A merchant who has switched every ProShip
    courier off sees a picker with only their own couriers in it and no clue that a hundred more
    exist one screen away. Counting the omissions lets the picker say so instead.
  */
  const withheld = useMemo(() => {
    const off = rows.filter((r) => !r.enabled);
    return {
      proship: off.filter((r) => r.owner === OWNER.PROSHIP).length,
      direct: off.filter((r) => r.owner === OWNER.DIRECT).length,
      total: off.length,
    };
  }, [rows]);

  return { pool, withheld, isLoading, isError };
}

/** How many of the five dimensions this account supplies itself. */
export const ownConfigCount = (account) =>
  SETUP_DIMENSIONS.filter((d) => account?.config?.[d.key] === 'OWN').length;

/** Read-only helpers for pages that need a value outside React state. */
export const readSlice = getSlice;
export const writeSlice = setSlice;

/**
 * ProShip's own pincode reach, from the serviceability-intel API.
 *
 * PROVENANCE: the call and the response shape are taken from useServiceabilityCoverage in
 * components/serviceability/index.js, which is where the panel already reads this. Only the one
 * figure this module needs is kept — unique forward delivery pincodes — rather than the full
 * breakdown that page renders.
 *
 * ROLE-GATED, AND THAT IS HANDLED, NOT PAPERED OVER. These endpoints may return 403 or an empty
 * payload for a merchant. `pincodes` stays null in that case and the card says the figure is
 * unavailable. Substituting a marketing number here would be inventing the size of a gap the
 * merchant is being asked to close, which is the one number that must never be made up.
 */
export function useProshipReach() {
  const [pincodes, setPincodes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    Promise.all([
      getGeneric(`${apiConstants.SERVICEABILITY_COVERAGE_DELIVERY}?viewMode=STYPE`),
      getGeneric(apiConstants.SERVICEABILITY_TRENDS_CUTS),
    ])
      .then(([styRes, cutsRes]) => {
        if (!live) return;
        const cuts = cutsRes?.data?.responseObj?.cuts || [];
        const forward = cuts.find((c) => c.dimension === 'DIRECTION' && c.cut === 'FORWARD');
        // The forward cut is the like-for-like figure: a merchant's uploaded coverage is forward
        // delivery pincodes, so the universe count would be comparing reach against possibility.
        const universe =
          styRes?.data?.responseObj?.universePinCount || cutsRes?.data?.responseObj?.universePinCount;
        const value = forward?.pinsServiceable || 0;
        setPincodes(value > 0 ? value : null);
        if (!value && universe) setPincodes(null);
      })
      .catch(() => {
        if (live) setPincodes(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  return { pincodes, loading };
}

/**
 * Shipments that could not be processed, and how many failed on coverage alone.
 *
 * REAL, from order/search with orderSubStatus = UNPROCESSED — the same filter the Unprocessed
 * Shipments page uses, so the count here and the list there cannot disagree.
 *
 * Only the first page of rows is read, because the count comes back separately and the failure
 * split does not need every row to be useful. `sampled` says so plainly: where the split is drawn
 * from fewer rows than the total, the card reports a proportion rather than passing off a partial
 * count as the whole.
 */
/** Rows read to split the failures. The count comes back whole regardless of this. */
const SAMPLE_LIMIT = 200;

/*
  Everything this hook does not read. Deliberately not imported from orders/list2 — that module's
  `exclude` is tuned for the shipment grid and would silently change what arrives here whenever a
  column is added there.
*/
const UNPROCESSED_EXCLUDES = [
  'itemList', 'order_history', 'orderManifest', 'courier', 'courierId', 'courierDetail',
  'courierPricing', 'courierPricingRTO', 'merchant', 'merchantPricing', 'merchantDetail',
  'customerDetail', 'customer_detail', 'shipmentDetail', 'invoiceUrl', 'label_url',
  'mongoLabelSignedUrlDetails', 'mongoInvoiceSignedUrlDetails', 'cpErrorList', 'returnInfo',
].join(',');

export function useUnprocessedShipments() {
  const merchant = getMerchant();
  const merchantId = merchant?.id || '';

  const [state, setState] = useState({ total: 0, coverage: 0, inspected: 0, loading: true });

  useEffect(() => {
    if (!merchantId) {
      setState({ total: 0, coverage: 0, inspected: 0, loading: false });
      return undefined;
    }
    let live = true;

    postGeneric(apiConstants.ORDER_LISTS, {
      // Same two filters the Unprocessed Shipments page applies, in the same payload shape
      // (offset/limit/andfilter/excludes) — see components/orders/list2/StuckOrders.js.
      andfilter: [
        { field: 'orderSubStatus', operator: 'eq', value: 'UNPROCESSED' },
        { field: 'merchant.$id', operator: 'eq', value: merchantId },
      ],
      offset: 0,
      limit: SAMPLE_LIMIT,
      // Only failureCategory is read, so the rest of a shipment document is dead weight over the
      // wire on a page the merchant loads first.
      excludes: UNPROCESSED_EXCLUDES,
    })
      .then((res) => {
        if (!live) return;
        const rows = res?.data?.data || [];
        const total = res?.data?.count ?? rows.length;
        const coverage = rows.filter((r) => isCoverageFailure(r?.failureCategory)).length;
        setState({ total, coverage, inspected: rows.length, loading: false });
      })
      .catch(() => {
        if (live) setState({ total: 0, coverage: 0, inspected: 0, loading: false });
      });

    return () => {
      live = false;
    };
  }, [merchantId]);

  return {
    ...state,
    /** True when the split is drawn from a sample, so the card can say share not count. */
    sampled: state.total > state.inspected,
  };
}

/**
 * The three findings the Home page shows about running without ProShip's network.
 *
 * One hook rather than three so the page asks one question and the conditions for showing each
 * card live beside the data that decides them.
 */
/**
 * Every shipment this merchant has, ever.
 *
 * The count only — limit 1 and the whole document excluded, because `count` comes back beside the
 * rows and one row is the cheapest way to be told it. Account-wide, not Direct-only: the hero
 * figure it feeds is "total shipments", and scoping it to Direct would make the unprocessed count
 * beside it — which is account-wide — a share of a different denominator.
 */
export function useTotalShipments() {
  const merchant = getMerchant();
  const merchantId = merchant?.id || '';
  const [state, setState] = useState({ total: null, loading: true });

  useEffect(() => {
    if (!merchantId) {
      setState({ total: null, loading: false });
      return undefined;
    }
    let live = true;

    postGeneric(apiConstants.ORDER_LISTS, {
      andfilter: [{ field: 'merchant.$id', operator: 'eq', value: merchantId }],
      offset: 0,
      limit: 1,
      excludes: UNPROCESSED_EXCLUDES,
    })
      .then((res) => {
        if (!live) return;
        const total = res?.data?.count;
        setState({ total: Number.isFinite(total) ? total : null, loading: false });
      })
      .catch(() => {
        if (live) setState({ total: null, loading: false });
      });

    return () => {
      live = false;
    };
  }, [merchantId]);

  return state;
}

export function useDirectOpportunity() {
  const { rows } = useCourierAvailability();
  const { pincodes: proshipPincodes, loading: reachLoading } = useProshipReach();
  const unprocessed = useUnprocessedShipments();
  const shipments = useTotalShipments();
  const [lanes] = useSlice('serviceabilityLanes');

  const own = useMemo(() => ownCoverage(lanes), [lanes]);
  const lines = useMemo(
    () => serviceLineGap(rows, { OWNER, CREDENTIAL_STATE }),
    [rows]
  );
  const reach = useMemo(() => reachGap(own, proshipPincodes), [own, proshipPincodes]);

  const proshipRows = rows.filter((r) => r.owner === OWNER.PROSHIP);
  const proshipOn = proshipRows.filter((r) => r.enabled).length;
  const proshipTotal = proshipRows.length;

  /*
    Only worth saying anything while some of ProShip's network is switched off — wholly or partly.
    With every ProShip courier on, the merchant is already using the wider network and a card
    telling them what they are missing is telling them about something they have.

    proshipTotal > 0 guards the catalogue still loading: nothing is "off" before anything is known,
    and without this the cards would flash on every page load.
  */
  const anyProshipOff = proshipTotal > 0 && proshipOn < proshipTotal;

  return {
    own,
    lines,
    reach,
    reachLoading,
    unprocessed,
    shipments,
    proshipOn,
    proshipTotal,
    anyProshipOff,
    /** Ready to render, in the same shape as the page's other stat cards. */
    stats: anyProshipOff
      ? opportunityStats({ reach, unprocessed, lines, proshipOn })
      : [],
  };
}
